import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as St from './SideItemInputHeight.styles';

import { UNIT_MEASURE } from '~/core/constants';
import { SideItemType } from '~/core/enums';
import formatUOM from '~/core/helpers/formatUOM';
import { SideItemInfoModel } from '~/core/models';
import DesignInformationBtn from '~organisms/DesignInformationBtn';

type SideItemInputHeightProps = {
  className?: string;
  value: number;
  limitsWarning?: string;
  onChange: (height: number) => void;
  setIsInfoModalOpen: (isOpen: boolean) => void;
  sideItem: SideItemInfoModel;
};

const SideItemInputHeight = ({
  className,
  value: initialHeightValue,
  limitsWarning = '',
  onChange,
  setIsInfoModalOpen,
  sideItem,
}: SideItemInputHeightProps): JSX.Element => {
  const { t } = useTranslation();
  const [enteredHeight, setEnteredHeight] = useState<number | null>(null);
  const inputElement = useRef<HTMLInputElement>(null);

  /**
   * Generalized event handler to support both blur and enter key press events
   */
  const handleInputEvent = (
    e:
      | React.FocusEvent<HTMLInputElement>
      | React.KeyboardEvent<HTMLInputElement>
  ): void => {
    if ('key' in e && e.key !== 'Enter') {
      return;
    }

    e.preventDefault();
    onChange(enteredHeight ?? initialHeightValue);
    setEnteredHeight(null);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // In case sideItemHeight is different from the external value, triggers the onChange method
    if (enteredHeight !== initialHeightValue) {
      handleInputEvent(e);
    }
  };

  return (
    <>
      <St.Container className={className}>
        <St.Label>
          {t('SCROLLABLE_AREA.COUNTERTOP.HEIGHT')}&nbsp;(
          {formatUOM(UNIT_MEASURE.SIDE_WALLS)})
        </St.Label>
        <St.InputNumber
          ref={inputElement}
          type="number"
          value={enteredHeight ?? initialHeightValue}
          onChange={(value) => value !== null && setEnteredHeight(value)}
          onClick={(e) => e.stopPropagation()}
          onBlur={handleBlur}
          onPressEnter={() => inputElement.current?.blur()}
        />
        {(sideItem.sideType === SideItemType.WALL_SIDE ||
          sideItem.sideType === SideItemType.WATERFALL) && (
          <DesignInformationBtn setIsInfoModalOpen={setIsInfoModalOpen} />
        )}
      </St.Container>

      {limitsWarning && <St.LimitsWarning>{limitsWarning}</St.LimitsWarning>}
    </>
  );
};

export default SideItemInputHeight;
